import { CircularProgress } from "@mui/material";
import { get, includes, map, toLower } from "lodash";
import { useEffect } from "react";
import { connect } from "react-redux";
import RestrictedAccess from "../../../common/js/components/RestrictedAccess";
import Comments from "../comments/CommentsContainer";
import comments_constants from "../comments/constants";
import comments_operations from "../comments/ducks/operations";
import DocumentDrop from "../documents/beta/DocumentDropContainerBeta";
import documentTypes from "../documents/documentTypes";
import searchOperations from "../search/ducks/operations";
import utils from "../utils/index";
import { displaySellerLandlordCompData } from "../utils/operations";
import { documentDisabledStatuses } from "./constants";
import ConsultingDealContainer from "./ConsultingDealContainer";
import actions from "./ducks/actions";
import operations from "./ducks/operations";
import VoucherForm from "./VoucherForm";
import VoucherToolbar from "./VoucherToolbar";

const VoucherContainer = (props) => {
    const { determineDocumentOptions, getVoucher, handleClearDealState, userProfile, voucher, voucherAccessAllowed } =
        props;
    useEffect(() => {
        const { id } = props.match.params;
        getVoucher(id);
        return () => {
            handleClearDealState();
        };
    }, []);

    const { id } = props.match.params;
    const isBroker = userProfile.role === "broker";
    const isClosed = voucher.voucherStatus === "Closed";

    return voucher.voucherTransactionType ? (
        voucherAccessAllowed ? (
            voucher.voucherTransactionType === "Consulting" ? (
                <ConsultingDealContainer {...props} isBrokerAndClosedVoucher={isBroker && isClosed} />
            ) : (
                <div>
                    <div style={{ minWidth: "800px" }}>
                        <VoucherToolbar {...props} />
                        <DocumentDrop
                            entityKey={id}
                            type="voucher"
                            documentTypes={toLower(get(voucher, "voucherTransactionType", null))}
                            voucherStatus={voucher.voucherStatus}
                            options={determineDocumentOptions}
                            formDisabled={get(
                                documentDisabledStatuses,
                                `${userProfile.role}.${voucher.voucherStatus}`,
                                true
                            )}
                            errors={props.utilsErrors.documents}
                            parentType="VOUCHER"
                            stateCode={get(voucher.deal, "listing.property.primaryAddress.city.state.stateCode", null)}
                            isBrokerAndClosedVoucher={isBroker && isClosed}
                        />
                        <VoucherForm {...props} />
                        <div style={{ minWidth: "50%" }}>
                            <Comments
                                parentRecordType={comments_constants.VOUCHER_PARENT_RECORD_TYPE}
                                parentRecordId={id}
                            />
                        </div>
                    </div>
                </div>
            )
        ) : (
            <RestrictedAccess message="You do not have permission to view this voucher" />
        )
    ) : (
        <div style={{ textAlign: "center", verticalAlign: "center" }}>
            <CircularProgress style={{ position: "absolute", top: "50%" }} color="primary" />
        </div>
    );
};

const determineDocumentOptions = (role, voucher, authRole) => {
    const allDocTypes = get(documentTypes, "allDocTypesList");
    const isCDT = includes(["dataEntry", "dataAdmin"], role);
    const isBroker = includes(["broker"], role);
    const isAcc = includes(["accounting"], role);

    let options = {
        canView: [],
        canDelete: [],
        canUpload: [],
    };

    let voucherDocTypes = [];
    if (includes(["lease", "for lease"], get(voucher, "voucherTransactionType", "").toLowerCase())) {
        voucherDocTypes = [
            "COMMISSION_PAYMENT",
            "FULLY_EXECUTED_LEASE",
            "INVOICE",
            "MASTER_LEASE_IF_SUBLEASE",
            "W9",
            "OTHER",
        ];
    } else {
        voucherDocTypes = ["COMMISSION_PAYMENT", "INVOICE", "W9", "OTHER"];
    }

    if (!includes(["Closed"], voucher.voucherStatus)) {
        options.canUpload = ["COMMISSION_PAYMENT"];
    }
    if (includes(["Awaiting Comp"], voucher.voucherStatus) && isCDT) {
        options.canView = [...allDocTypes];
        options.canDelete = [...voucherDocTypes];
        options.canUpload = [...voucherDocTypes];
    } else if (includes(["Awaiting Payment"], voucher.voucherStatus)) {
        if (isCDT || isAcc) {
            options.canView = [...allDocTypes];
            options.canDelete = [...voucherDocTypes];
            options.canUpload = [...voucherDocTypes];
        } else if (isBroker) {
            options.canView = [...allDocTypes];
            options.canDelete = [];
            options.canUpload = [...voucherDocTypes];
        } else {
            options.canView = [...allDocTypes];
            options.canDelete = [...voucherDocTypes];
            options.canUpload = [...voucherDocTypes];
        }
    } else if (includes(["Ready For Accounting"], voucher.voucherStatus)) {
        if (isCDT || isAcc) {
            options.canView = [...allDocTypes];
            options.canDelete = [...voucherDocTypes];
            options.canUpload = [...voucherDocTypes];
        } else if (isBroker) {
            options.canView = [...allDocTypes];
            options.canDelete = [];
            options.canUpload = [];
        } else {
            options.canView = [...allDocTypes];
            options.canDelete = [...voucherDocTypes];
            options.canUpload = [...voucherDocTypes];
        }
    } else if (includes(["Closed"], voucher.voucherStatus)) {
        if (authRole === "dataAdmin") {
            options.canView = [...allDocTypes];
            options.canDelete = [...allDocTypes];
            options.canUpload = [...voucherDocTypes];
        } else {
            options.canView = [...allDocTypes];
            options.canDelete = [];
            options.canUpload = [...voucherDocTypes];
        }
    } else {
        options = null;
    }

    return options;
};

const voucherAccessAllowed = (user, role, personKey, brokerCommissions, voucher) => {
    const brokerPersonKeys = map(brokerCommissions, (commission) => {
        return get(commission, "brokerAgent.person.personKey");
    });
    const isBrokerOnDeal = includes(brokerPersonKeys, personKey);
    const isCreatedByUser = user === voucher.auditCreatedBy;
    const isDataEntryOwner = voucher.dataEntryOwner && user === voucher.dataEntryOwner;
    const isLastModifiedByUser = user === voucher.auditLastModifiedBy;
    const isPortlandDeal = includes("portlandBrokerAdmin", role) && voucher.deal.portlandDeal;
    const isArizonaDeal = includes("arizonaBrokerAdmin", role) && voucher.deal.arizonaDeal;

    return (
        isBrokerOnDeal ||
        isCreatedByUser ||
        isDataEntryOwner ||
        isLastModifiedByUser ||
        isPortlandDeal ||
        isArizonaDeal ||
        includes(["dataEntry", "dataAdmin", "accounting", "evp"], role)
    );
};

const mapStateToProps = (state) => {
    return {
        userProfile: state.authReducer.userProfile,
        authenticatedUserProfile: state.authReducer.authenticatedUserProfile,
        valid: state.authReducer.validFormValues,
        snackbarOpen: state.authReducer.snackbarOpen,
        isSaving: state.utilsReducer.isSaving,
        saveError: state.voucherReducer.saveError ? state.voucherReducer.saveError : null,
        lastSaved: state.voucherReducer.lastSaved ? state.voucherReducer.lastSaved : null,
        brokerCommissions: state.voucherReducer.voucher.brokerCommissions,
        outsideSplits: state.voucherReducer.outsideSplits,
        outsideSplitModal: state.voucherReducer.outsideSplitModal,
        numComments: state.commentsReducer.comments.length,
        voucher: state.voucherReducer.voucher,
        deal: state.voucherReducer.voucher.deal,
        errors: state.voucherReducer.errors,
        utilsErrors: state.utilsReducer.errors,
        outsideSplitsPresent: state.voucherReducer.outsideSplitsPresent,
        searchResults: state.utilsReducer.searchResults,
        autocompleteResults: state.searchReducer.autocompleteResults,
        snackbar: state.utilsReducer.snackbar,
        documentWarning: state.voucherReducer.documentWarning,
        confirmDialog: state.utilsReducer.confirmDialog,
        elementsSaving: state.utilsReducer.elementsSaving,
        usedDocTypes: state.documentsReducer.usedDocTypes,
        voucherAccessAllowed: voucherAccessAllowed(
            state.authReducer.userProfile.user,
            state.authReducer.userProfile.role,
            state.authReducer.userProfile.personKey,
            state.voucherReducer.voucher.brokerCommissions,
            state.voucherReducer.voucher
        ),
        determineDocumentOptions: determineDocumentOptions(
            state.authReducer.userProfile.role,
            get(state.voucherReducer, "voucher", {}),
            get(state.authReducer.authenticatedUserProfile, "role", null)
        ),
        displaySellerLandlordCompData: displaySellerLandlordCompData(
            state.authReducer.userProfile.user,
            state.authReducer.userProfile.role,
            state.authReducer.userProfile.personKey,
            state.dealReducer.brokersOnDeal,
            state.dealReducer.deal
        ),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getVoucher: (voucherKey) => {
            dispatch(operations.handleGetVoucher(voucherKey));
        },
        handleSubmit: (save, callback, validateDocs = true) => {
            dispatch(utils.operations.toggleConfirm());
            return dispatch(
                operations.handleSubmit(save, callback, "submit", {
                    validateDocs: validateDocs,
                    validateForm: true,
                    validateNonRequired: false,
                })
            );
        },
        handleSave: () => {
            dispatch(
                operations.handleSubmit(true, null, "saveAndContinue", {
                    validateDocs: false,
                    validateForm: false,
                    validateNonRequired: true,
                })
            );
        },
        handleSaveAndExit: () => {
            return dispatch(
                operations.handleSubmit(true, null, "saveAndExit", {
                    validateDocs: false,
                    validateForm: false,
                    validateNonRequired: true,
                })
            );
        },
        // Unused method, remove when time permits
        handleSubmitToReadyForPayment: (save, callback) => {
            dispatch(
                operations.handleSubmit(save, callback, "submit", {
                    validateDocs: true,
                    validateForm: true,
                    validateNonRequired: false,
                })
            );
        },
        toggleSnackBar: (open, variant, message) => {
            dispatch(utils.operations.snackbar(open, variant, message));
        },
        handleAutosaveNotSuccess: (isSaving, err) => {
            dispatch(operations.handleAutosaveNotSuccess(isSaving, err));
        },
        handleAutosaveSuccess: () => {
            dispatch(operations.handleAutosaveSuccess());
        },
        handleAddBrokerCommission: () => {
            dispatch(operations.handleAddBrokerCommission());
        },
        handleCreateOutsideSplitDraft: (enableFreeFormSearch, commissionType) => {
            dispatch(operations.createOutsideSplitDraft(enableFreeFormSearch, commissionType));
        },
        handleSaveOutsideSplit: (key, split) => {
            dispatch(operations.saveOutsideSplit(key, split));
        },
        handleDeleteOutsideSplit: (splitKey) => {
            dispatch(operations.deleteOutsideSplit(splitKey));
        },
        toggleOutsideSplitModal: (options) => {
            dispatch(operations.toggleOutsideSplitModal(options));
        },
        handleAddRunner: (parent) => {
            dispatch(operations.handleAddRunner(parent));
        },
        handleRemoveCommissionOrRunner: (options) => {
            dispatch(operations.handleRemoveCommissionOrRunner(options.key, options.parent, options.outside));
        },
        updateSplit: (splitKey, field, value, source) => {
            dispatch(operations.updateSplit(splitKey, field, value, source));
        },
        updateRunner: (parent, key, field, value) => {
            dispatch(operations.updateRunner(parent, key, field, value));
        },
        toggleComments: () => {
            dispatch(comments_operations.toggleComments());
        },
        setInitialPaymentMethod: (value) => {
            dispatch(operations.voucherChange("paymentMethod", value));
        },
        handleVoucherChange: (event) => {
            dispatch(operations.voucherChange(event.target.id, event.target.value));
        },
        handleCheckboxClick: (name, event) => {
            dispatch(operations.handleInput("voucher." + name, event.target.checked));
        },
        handleVoucherChangeAndCalculateSplits: (km, event) => {
            dispatch(operations.voucherChangeAndCalculateSplits(event.target.id, event.target.value, km));
        },
        determineOutsidePresent: (isPresent) => {
            dispatch(actions.determineOutsidePresent(isPresent));
        },
        handleDeleteVoucher: (voucherKey) => {
            return dispatch(operations.deleteVoucher(voucherKey));
        },
        handleSearchPerson: (inputValue) => {
            dispatch(operations.searchPerson(inputValue));
        },
        handleSearchOutsideBroker: (inputValue) => {
            dispatch(searchOperations.searchOutsideBroker(inputValue));
        },
        handleSearchCompany: (inputValue, companyType) => {
            dispatch(searchOperations.searchCompany(inputValue, companyType));
        },
        handleSearchBroker: (inputValue) => {
            dispatch(searchOperations.autoCompleteBroker(inputValue));
        },
        handleDocumentWarning: (open, proceed, docs) => {
            dispatch(operations.handleDocumentWarning(open, proceed, docs));
        },
        handleConfirmWarning: (callback) => {
            dispatch(operations.handleDocumentWarning(false, true, [], null));
            dispatch(
                operations.saveVoucher(callback, "submit", true, {
                    validateDocs: false,
                    validateForm: true,
                    validateNonRequired: false,
                })
            );
        },
        toggleConfirm: (onSubmit) => {
            dispatch(utils.operations.toggleConfirm(onSubmit));
        },
        handleInput: (path, value) => {
            dispatch(operations.handleInput("voucher." + path, value));
        },
        handleReducerLevelInput: (path, value) => {
            dispatch(operations.handleInput(path, value));
        },
        handleClearDealState: () => {
            dispatch(
                operations.handleInput("voucher", {
                    deal: {},
                    brokerCommissions: {},
                })
            );
        },
        handleAddArrayElement: (path) => {
            dispatch(operations.addArrayElement("voucher.deal." + path));
        },
        handleDeleteArrayElement: (path, index) => {
            dispatch(operations.deleteArrayElement("voucher.deal." + path, index));
        },
        handleUpdateArrayElement: (arrayPath, index, field, value) => {
            dispatch(operations.handleInput("voucher." + arrayPath + "[" + index + "]." + field, value));
        },
        handleCalculateSplits: () => {
            dispatch(operations.handleCalculateSplits());
        },
        handleSearchKmBrokers: (input) => {
            dispatch(searchOperations.searchKMBrokers(input));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(VoucherContainer);
